import {
  ApiLocation,
  ApiChannel,
  ApiEngagementRule,
  ApiClassificationGroup,
  ApiClassification,
  ApiConversion,
  ApiProject,
  ApiAudience,
  ApiPageType,
  ApiLeadStatusRule,
  ApiLeadScoreAdjustmentRule,
  ApiReportResponse,
  ApiEffectivenessRule,
  ApiSessionEffectivenessRule,
  ApiLocationPageTagger,
  ApiAPITokenResponse,
  ApiBucketResponse,
  ApiTabElementResponse,
  ApiCSVUploadSession,
  ApiCSVRule,
  ApiUser,
  ApiProduct,
  ApiUserOauthClient,
  ApiSync,
  ApiService,
  ApiPageviewMetaTagger,
  ApiAttributionTag,
  ApiJob,
  ApiSimpleComponent,
  ApiSimpleCampaign,
  ApiMedia,
  ApiDomainResponse,
  ApiReadLanderResponse,
  ApiSimpleUTM,
  ApiDomain,
  ApiSimpleSubscription,
  ApiSimpleContract,
  ApiSimpleProduct,
  ApiSimpleProjectSubscription,
  ApiSimpleSourceCategory,
  ApiSimpleMetricLabel,
  ApiSimpleDimensionLabel,
  ApiSimpleOnboarding,
  ApiUTMEntity,
  ApiProjectSetting,
  ApiAISession,
  ApiSimpleSiteinfoLabel,
  ApiBilling,
  ApiAIPrompt,
  ApiCredentialsRequestResponse,
  ApiSimpleTenant,
  ApiIncrementalityTest,
  ApiQuickInsightResult,
  ApiDashboardProfileResponse,
  ApiUserDashboardProfile,
  ApiAlertResponse,
} from "@incendium/api";
import { useSelectedProject } from "./useSelectedProject";
import { useDispatch, useSelector } from "react-redux";
import { ThunkDispatch } from "@reduxjs/toolkit";
import {
  CallbackOrVal,
  IChart,
  IStoreState,
  TFilterableDimensions,
} from "Interfaces";
import { fetchStateThunk, setApplicationState } from "Actions";
import { get } from "lodash";
import { useMount } from "./useMount";
import { useEffect, useRef, useCallback } from "react";
import { IHasSalesLastYear } from "../features/analytics/hooks/useHasHadSales";

// export type TMappedApiUserOauthClient = {
//   [key in ApiService]: ApiUserOauthClient[];
// };
export type TMappedApiUserOauthClient = Record<
  ApiService,
  ApiUserOauthClient[]
>;

type ApiDataSingle =
  | ApiUser
  | ApiLocation
  | ApiChannel
  | ApiBucketResponse
  | ApiEngagementRule
  | ApiEffectivenessRule
  | ApiSessionEffectivenessRule
  | ApiClassificationGroup
  | ApiClassification
  | ApiConversion
  | ApiAudience
  | ApiPageType
  | ApiAlertResponse
  | ApiLeadStatusRule
  | ApiLeadScoreAdjustmentRule
  | ApiReportResponse
  | ApiLocationPageTagger
  | ApiAPITokenResponse
  | ApiTabElementResponse
  | ApiCSVUploadSession
  | IChart
  | ApiCSVRule
  | ApiProduct
  | ApiSync
  | ApiPageviewMetaTagger
  | ApiAttributionTag
  | ApiJob
  | ApiSimpleComponent
  | ApiSimpleCampaign
  | ApiMedia
  | ApiDomainResponse
  | ApiReadLanderResponse
  | ApiDomain
  | TFilterableDimensions
  | ApiSimpleUTM
  | ApiSimpleSubscription
  | ApiSimpleContract
  | ApiSimpleProjectSubscription
  | ApiSimpleProduct
  | ApiSimpleSourceCategory
  | ApiSimpleMetricLabel
  | ApiSimpleDimensionLabel
  | ApiSimpleOnboarding
  | ApiProjectSetting
  | ApiUTMEntity
  | TMappedApiUserOauthClient
  | IHasSalesLastYear
  | ApiAISession
  | ApiSimpleSiteinfoLabel
  | ApiBilling
  | ApiAIPrompt
  | ApiCredentialsRequestResponse
  | IHasSalesLastYear
  | ApiSimpleTenant
  | ApiIncrementalityTest
  | ApiQuickInsightResult
  | ApiDashboardProfileResponse
  | ApiUserDashboardProfile;

type ApiData = ApiDataSingle | ApiDataSingle[];

// type StateData<T> = T extends ApiDataSingle ? T | T[] : T;

// Base hook for shared state management
function useBaseSharedState<T>(
  key: Exclude<keyof IStoreState["application"], "clients" | "projects">,
  clientId: string | null,
  projectId: string | null,
  defaultVal: T,
): {
  state: T;
  setState: (val: CallbackOrVal<T>) => void;
  dispatch: ThunkDispatch<IStoreState, null, any>;
  loading: boolean;
  fetched: boolean;
} {
  const dispatch = useDispatch();
  const appState = useSelector((state: IStoreState) => state.application[key]);

  const state = (
    clientId === null
      ? get(appState, ["global", "global", "state"], defaultVal)
      : get(appState, [clientId, projectId as string, "state"], defaultVal)
  ) as T;

  const status =
    clientId === null
      ? get(appState, ["global", "global", "status"])
      : get(appState, [clientId, projectId as string, "status"]);

  const loading = ["UNFETCHED", "FETCHING"].indexOf(status) >= 0;
  const fetched = ["FETCH_SUCCESS"].indexOf(status) >= 0;

  const setState = (val: CallbackOrVal<T>) => {
    dispatch(setApplicationState(clientId, projectId, key, val));
  };

  return { state, setState, dispatch, loading, fetched };
}

// Hook for global state
export function useGlobalSharedState<T extends ApiData>(
  key: Exclude<keyof IStoreState["application"], "clients" | "projects">,
  defaultVal: T,
  fetch: () => Promise<T>,
): {
  state: T;
  setState: (val: CallbackOrVal<T>) => void;
  refetch: (refresh?: boolean) => Promise<any>;
  loading: boolean;
  fetched: boolean;
} {
  const { state, setState, dispatch, loading, fetched } = useBaseSharedState(
    key,
    null,
    null,
    defaultVal,
  );

  // Watch auth tokens to refetch when user logs back in after token expiration
  const tokens = useSelector((state: IStoreState) => state.auth.tokens);
  const prevTokensRef = useRef(tokens);

  const fetchFromApi = useCallback(async () => {
    try {
      const data = await fetch();
      setState(data);
      return Promise.resolve();
    } catch (error) {
      console.error(error);
      return Promise.resolve();
    }
  }, [fetch, setState]);

  const refetch = async (refresh = true) => {
    return dispatch(fetchStateThunk(null, null, key, fetchFromApi, refresh));
  };

  useMount(() => {
    refetch(false);
  });

  // Refetch when auth tokens change from null/undefined to valid (user logged back in)
  useEffect(() => {
    const prevTokens = prevTokensRef.current;
    const hasTokens = tokens && tokens.token;
    const hadTokens = prevTokens && prevTokens.token;

    // If tokens changed from invalid/null to valid, refetch the data
    if (!hadTokens && hasTokens) {
      dispatch(fetchStateThunk(null, null, key, fetchFromApi, false));
    }

    prevTokensRef.current = tokens;
  }, [tokens, dispatch, key, fetchFromApi]);

  return { state, setState, refetch, loading, fetched };
}

// Hook for project-scoped state
export function useProjectSharedState<T extends ApiData>(
  key: Exclude<keyof IStoreState["application"], "clients" | "projects">,
  defaultVal: T,
  fetch: (proj: ApiProject) => Promise<T>,
): {
  state: T;
  setState: (val: CallbackOrVal<T>) => void;
  refetch: (refresh?: boolean) => Promise<any>;
  loading: boolean;
  fetched: boolean;
} {
  const { selectedProject } = useSelectedProject();
  const clientId = selectedProject?.clientId?.toFixed() as string;
  const projectId = selectedProject?.id?.toFixed() as string;

  const { state, setState, dispatch, loading, fetched } = useBaseSharedState<T>(
    key,
    clientId,
    projectId,
    defaultVal,
  );

  const fetchFromApi = async () => {
    if (!selectedProject) return Promise.resolve();
    try {
      const data = await fetch(selectedProject);
      setState(data);
      return Promise.resolve();
    } catch (error) {
      console.error(error);
      return Promise.resolve();
    }
  };

  const refetch = async (refresh = true) => {
    return dispatch(
      fetchStateThunk(clientId, projectId, key, fetchFromApi, refresh),
    );
  };

  useMount(() => {
    refetch(false);
  });

  // Add effect to refetch when project changes
  useEffect(() => {
    if (selectedProject && clientId && projectId) {
      refetch(false);
    }
  }, [selectedProject?.id, clientId, projectId]);

  return { state, setState, refetch, loading, fetched };
}

// Hook for parent-child state relationships
export function useParentChildSharedState<
  TParent extends ApiData,
  TChild extends ApiData = TParent extends Array<infer U> ? U : TParent,
>(
  key: Exclude<keyof IStoreState["application"], "clients" | "projects">,
  defaultVal: TParent,
  defaultItemVal: TChild,
  itemId: number,
  fetch: (proj: ApiProject) => Promise<TParent>,
  fetchItem: (proj: ApiProject) => Promise<TChild>,
) {
  const { selectedProject } = useSelectedProject();
  const clientId = selectedProject?.clientId?.toFixed() as string;
  const projectId = selectedProject?.id?.toFixed() as string;

  const { state, setState, dispatch, loading, fetched } = useBaseSharedState(
    key,
    clientId,
    projectId,
    defaultVal,
  );

  const appState = useSelector((state: IStoreState) => state.application[key]);
  const childState = get(
    appState,
    [clientId, projectId, itemId || 0, "state"],
    defaultItemVal,
  );

  const setChildState = (val: CallbackOrVal<TChild>) => {
    dispatch(
      setApplicationState(
        clientId,
        projectId,
        key,
        val,
        isNaN(itemId) ? 0 : itemId,
      ),
    );
  };

  const fetchFromApi = async () => {
    if (!selectedProject) return Promise.resolve();
    try {
      const data = await fetch(selectedProject);
      setState(data);
      return Promise.resolve();
    } catch (error) {
      console.error(error);
      return Promise.resolve();
    }
  };

  const fetchItemFromApi = async () => {
    if (!selectedProject) return Promise.resolve();
    try {
      const data = await fetchItem(selectedProject);
      setChildState(data);
      return Promise.resolve();
    } catch (error) {
      console.error(error);
      return Promise.resolve();
    }
  };

  const refetch = async (refresh = true) => {
    await dispatch(
      fetchStateThunk(clientId, projectId, key, fetchFromApi, refresh),
    );

    if (isNaN(itemId) || itemId === 0) {
      return dispatch(
        fetchStateThunk(
          clientId,
          projectId,
          key,
          async () => {
            setChildState(defaultItemVal);
            return Promise.resolve();
          },
          refresh,
          0,
        ),
      );
    }

    return dispatch(
      fetchStateThunk(
        clientId,
        projectId,
        key,
        fetchItemFromApi,
        refresh,
        itemId,
      ),
    );
  };

  const resetEmpty = () => {
    dispatch(
      setApplicationState(clientId, projectId, key, defaultItemVal || {}, 0),
    );
  };

  useMount(() => {
    refetch(false);
  });

  return {
    state,
    setState,
    childState,
    setChildState,
    refetch,
    loading,
    fetched,
    resetEmpty,
  };
}
